import type { EstablishmentId, TenantId } from '@/types';

/**
 * Every cache key is prefixed with the tenant so a tenant switch can never surface another
 * tenant's cached response. Feature modules extend their own branch under `queryKeys`.
 */
export const queryKeys = {
  all: (tenantId: TenantId) => ['tenant', tenantId] as const,

  organization: (tenantId: TenantId) => [...queryKeys.all(tenantId), 'organization'] as const,

  catalog: (tenantId: TenantId, establishmentId: EstablishmentId) =>
    [...queryKeys.all(tenantId), 'catalog', establishmentId] as const,

  /**
   * Browsing the catalogue is not establishment-scoped: curation is published per customer, so
   * two establishments of the same tenant see the same items and must share one cache entry.
   */
  catalogBrowse: (tenantId: TenantId) => [...queryKeys.all(tenantId), 'catalog-browse'] as const,

  cart: (tenantId: TenantId, establishmentId: EstablishmentId) =>
    [...queryKeys.all(tenantId), 'cart', establishmentId] as const,

  orders: (tenantId: TenantId, establishmentId: EstablishmentId) =>
    [...queryKeys.all(tenantId), 'orders', establishmentId] as const,

  payments: (tenantId: TenantId) => [...queryKeys.all(tenantId), 'payments'] as const,

  /** Contracts and receivables (Sprint 8) — the term-checkout path reads and invalidates here. */
  contracts: (tenantId: TenantId) => [...queryKeys.all(tenantId), 'contracts'] as const,

  tracking: (tenantId: TenantId) => [...queryKeys.all(tenantId), 'tracking'] as const,

  backoffice: (tenantId: TenantId) => [...queryKeys.all(tenantId), 'backoffice'] as const,
} as const;
